import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A crossfading seasonal hero.
 *
 * Cycles through full-bleed seasonal panels, each of which is itself a link to that
 * season's collection. Rotation suspends while the pointer is inside the hero, while
 * keyboard focus is inside it, and while the tab is hidden, so a visitor reading a
 * caption is never moved along mid-thought.
 *
 * The season tabs below the panels select directly, so every season stays one click
 * away regardless of where the rotation happens to be.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement[]} [tabs]
 *
 * @extends {Component<Refs>}
 */
class SeasonalHero extends Component {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  /** @type {number} */
  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.toggleAttribute('no-rotate', !this.shouldAutoplay);
    this.#activate(this.#index);
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.suspend);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /** The panels being cycled. */
  get slides() {
    return this.refs.slides ?? [];
  }

  /** The season tabs, one per panel. */
  get tabs() {
    return this.refs.tabs ?? [];
  }

  /** Dwell time per season, in milliseconds. */
  get interval() {
    return Number.parseInt(this.getAttribute('autoplay') ?? '', 10) || 5500;
  }

  /**
   * Whether the hero should advance on its own. A hero that rotates without being
   * asked is motion the visitor did not request, so we leave it parked under
   * reduced-motion and let the tabs do the work.
   */
  get shouldAutoplay() {
    return this.slides.length > 1 && !prefersReducedMotion();
  }

  /** Starts the rotation. */
  play() {
    if (!this.shouldAutoplay) return;

    this.suspend();
    this.removeAttribute('paused');
    this.#restartProgress();
    this.#timer = setInterval(() => this.next(), this.interval);
  }

  /** Halts the rotation and freezes the progress indicator where it stands. */
  suspend = () => {
    clearInterval(this.#timer);
    this.#timer = undefined;
    if (this.shouldAutoplay) this.setAttribute('paused', '');
  };

  /** Resumes the rotation, restarting the current season's dwell from the top. */
  resume = () => {
    this.play();
  };

  /** Advances to the next season, wrapping at the end. */
  next() {
    this.#activate((this.#index + 1) % this.slides.length);
  }

  /** Steps back one season, wrapping at the start. */
  previous() {
    this.#activate((this.#index - 1 + this.slides.length) % this.slides.length);
  }

  /**
   * Jumps to a season. Bound declaratively from the tabs via `on:click="/select/0"`.
   *
   * @param {number|string} index - The zero-based index of the season to show.
   */
  select(index) {
    const next = Number.parseInt(String(index), 10);

    if (Number.isNaN(next) || next === this.#index) return;

    this.#activate(next);
    this.play();
  }

  /**
   * Reveals one season and hides the rest.
   *
   * @param {number} index
   */
  #activate(index) {
    const { slides, tabs } = this;

    if (!slides.length) return;

    this.#index = index;

    slides.forEach((slide, i) => {
      slide.toggleAttribute('data-active', i === index);
      slide.setAttribute('aria-hidden', String(i !== index));
    });

    tabs.forEach((tab, i) => {
      if (i === index) {
        tab.setAttribute('aria-current', 'true');
      } else {
        tab.removeAttribute('aria-current');
      }
    });

    this.#restartProgress();
  }

  /**
   * Replays the active tab's progress fill from zero. The fill is a CSS animation
   * scoped to the selected tab, so removing and reinstating it is what rewinds it.
   */
  #restartProgress() {
    const tab = this.tabs[this.#index];

    if (!tab || prefersReducedMotion()) return;

    const progress = tab.querySelector('.seasonal-hero__tab-progress');

    if (!(progress instanceof HTMLElement)) return;

    progress.style.animation = 'none';
    // Force a reflow so the animation is genuinely torn down before being reinstated.
    void progress.offsetWidth;
    progress.style.animation = '';
  }

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    const next = event.relatedTarget;

    if (next instanceof Node && this.contains(next)) return;

    this.resume();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.seasonal-hero__tabs')) return;

    if (event.key === 'ArrowRight') {
      this.next();
    } else if (event.key === 'ArrowLeft') {
      this.previous();
    } else {
      return;
    }

    event.preventDefault();
    this.tabs[this.#index]?.focus();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else {
      this.resume();
    }
  };
}

if (!customElements.get('seasonal-hero-component')) {
  customElements.define('seasonal-hero-component', SeasonalHero);
}
